import React, { useEffect, useState } from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import { useAuthContext } from '../../context/auth';
import { useClaimContext } from '../../context/claim';
import { usePolicyContext } from '../../context/policy';
import AuxDataView from '../auxData/AuxDataView';
import { StyledContentPane } from '../common';
import DisabledOverlay from '../DisabledOverlay';
import { useFastForm } from '../fastForm';
import { getClaimForm, getDefaultPolicyValues } from '../fastForm/formBuilders';
import LargeCenterSpinner from '../LargeCenterSpinner';
import ClaimLayout from '../layouts/ClaimLayout';
import LeftSidebarLayout from '../layouts/LeftSidebarLayout';
// import SubClaimRoutes from '../policy/Routes/SubClaimRoutes';
import ProtectedRoute from '../ProtectedRoute';
import { errorToast } from '../Toast';
import AddSubclaimView from './AddSubclaimView';
import ClaimDetails from './ClaimDetails';
import ClaimsSidebar from './ClaimsSidebar';
import Subclaim from './Subclaim';
import SubclaimsView from './SubclaimsView';

// Acts as a MainLayout
// route: `/policy/:policyId/claims/:claimId`
const ClaimRoutes = () => {
	const policyData = usePolicyContext();
	const authData = useAuthContext();
	const navigate = useNavigate();
	const claimData = useClaimContext();
	const params = useParams();
	const [loading, setLoading] = useState(true);
	const { claim, setClaim, setEditMode, setFields, fields } = claimData;
	const { productConfig } = policyData;
	const { timezone } = authData.auth;
	const { policyId, claimId } = params;
	const { claimConfiguration } = productConfig;
	const methods = useFastForm(fields, {}) as UseFormReturn<FieldValues, object>;
	const { reset } = methods;

	useEffect(() => {
		const populateFields = async () => {
			if (claimConfiguration && claim) {
				const fields = getClaimForm({
					claimConfiguration,
					claim,
					timezone,
				});
				const defaultValues = getDefaultPolicyValues(fields, authData);
				reset(defaultValues);
				setFields(fields);
			}
		};
		populateFields();
	}, [authData, claim, claimConfiguration, reset, setFields, timezone]);

	useEffect(() => {
		const fetchClaim = async () => {
			try {
				await claimData.loadData(claimId);
				setLoading(false);
			} catch (error) {
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
				errorToast('Failed to fetch necessary data');
			}
		};
		fetchClaim();
	}, [authData, claimId, policyId, setClaim, setEditMode]);

	if (loading || !claim || claim.locator !== claimId) {
		return <LargeCenterSpinner />;
	}

	const renderMain = () => {
		if (loading) {
			return <LargeCenterSpinner />;
		}

		return (
			<StyledContentPane>
				<ClaimLayout>
					<Routes>
						<Route path='details' element={<ClaimDetails />} />
						<Route path='subclaims'>
							<Route index element={<SubclaimsView />} />
							<Route path=':subclaimId'>
								<Route index element={<Subclaim />} />
								<Route
									path='auxData'
									element={
										<ProtectedRoute permission='auxData.fetch'>
											<AuxDataView
												navigate={navigate}
												entityPath='subclaimId'
												entityType='claim'
											/>
										</ProtectedRoute>
									}
								/>
							</Route>
						</Route>
						<Route
							path='addSubclaim'
							element={
								<ProtectedRoute permission='claim.update'>
									<AddSubclaimView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityPath='claimId'
										entityType='claim'
									/>
								</ProtectedRoute>
							}
						/>
						<Route path='*' element={<Navigate to='details' replace />} />
					</Routes>
				</ClaimLayout>
			</StyledContentPane>
		);
	};

	const renderSidebar = () => {
		if (loading) {
			return <DisabledOverlay />;
		}

		return <ClaimsSidebar />;
	};

	return (
		<FormProvider {...methods}>
			<LeftSidebarLayout
				leftSidebarContent={renderSidebar()}
				mainContent={renderMain()}
			/>
		</FormProvider>
	);
};

const ClaimRoutesWithPolicyData = () => {
	const policyData = usePolicyContext();
	const params = useParams();
	const [loading, setLoading] = useState(false);
	const { policyId } = params;
	const { loadData, policy, productConfig } = policyData;

	useEffect(() => {
		const initializePolicyData = async () => {
			if (!loading) {
				if (!policy || policy?.locator !== policyId || !productConfig) {
					setLoading(true);
					await loadData(policyId);
					setLoading(false);
				}
			}
		};
		initializePolicyData();
	}, [loadData, loading, policy, policyId, productConfig]);

	if (!policy || policy?.locator !== policyId || !productConfig) {
		return <LargeCenterSpinner />;
	}

	return <ClaimRoutes />;
};

export default ClaimRoutesWithPolicyData;
